import type { IntlShape } from 'react-intl'
import { getFlightDetailsWithDedup } from 'api/order'
import { arrayToMap, firstAndLast, nonNullable } from 'lib/array/arrayUtils'
import { formatMomentInputAsIsoDate } from 'lib/datetime/dateUtils'
import { isItemCancelled } from 'lib/order/orderUtils'
import { reportClientError } from 'services/errorReportingService'
import type { OfferTypeForTripItemTypes } from 'tripPlanner/hooks/api/common'
import { hasActiveOrderItems } from 'tripPlanner/hooks/orderChatTripEligibility'
import {
  serialiseBookedFlightContext,
  serialiseBookingContext,
  type BookedFlightReference,
  type BookingContextSection,
  type ExperienceBookingContextSection,
  type FlightBookingContextSection,
  type HotelBookingContextSection,
} from 'tripPlanner/utils/bookingContext'
import {
  appendContextTag,
  TRIP_PLANNER_ACTION_CONTEXT_LABEL,
} from 'tripPlanner/utils/contextTag'

export type SelectedBooking = {
  order: App.Order
  offers: Array<OfferTypeForTripItemTypes>
  selectionKey: string
  bookingItemId: string
}

function formatAirportLabel(airportName?: string, airportCode?: string): string | undefined {
  if (airportName && airportCode) {
    return `${airportName} (${airportCode})`
  }

  return airportName || airportCode
}

function getRouteEndpointLabels(flights: Array<App.FlightV2>): { fromLabel?: string, toLabel?: string } {
  const endpoints = firstAndLast(flights)
  if (!endpoints) {
    return {}
  }

  return {
    fromLabel: formatAirportLabel(endpoints.first.departureAirportName, endpoints.first.departureAirport),
    toLabel: formatAirportLabel(endpoints.last.arrivalAirportName, endpoints.last.arrivalAirport),
  }
}

function buildFlightRouteLabel(flights: Array<App.FlightV2>): string | undefined {
  const { fromLabel, toLabel } = getRouteEndpointLabels(flights)
  return fromLabel && toLabel ? `${fromLabel} -> ${toLabel}` : undefined
}

function getLeHotelPropertyName(
  orderItem: App.OrderItem,
  offer: OfferTypeForTripItemTypes | undefined,
): string | undefined {
  if (orderItem.offer.property?.name) {
    return orderItem.offer.property.name
  }

  if (offer?.type !== 'hotel') {
    return undefined
  }

  if ('property' in offer) {
    return offer.property?.name
  }

  return undefined
}

function isFlightOnlyOrderForChat(order: App.Order): boolean {
  return hasActiveOrderItems(order.flightItems) &&
    !hasActiveOrderItems(order.items) &&
    !hasActiveOrderItems(order.bedbankItems) &&
    !hasActiveOrderItems(order.experienceItems)
}

type ScopedOrderItems = Partial<Pick<App.Order, 'items' | 'bedbankItems' | 'flightItems' | 'experienceItems'>>

function buildScopedOrder(order: App.Order, scopedItems: ScopedOrderItems): App.Order {
  return {
    ...order,
    items: [],
    bedbankItems: [],
    flightItems: [],
    experienceItems: [],
    tourItems: [],
    cruiseItems: [],
    transferItems: [],
    carHireItems: [],
    giftCardItems: [],
    insuranceItems: [],
    addonItems: [],
    merchandiseItems: [],
    merchandiseShippingItems: [],
    ...scopedItems,
  }
}

function getSelectedBookedFlights(
  selectedBookings: Array<SelectedBooking>,
): Array<BookedFlightReference> {
  return selectedBookings.flatMap(({ order }) =>
    order.flightItems
      .filter((item) => !isItemCancelled(item))
      .map((orderFlightItem) => ({
        kind: 'bookedFlight',
        orderId: order.id,
        orderFlightItemId: orderFlightItem.id,
      })),
  )
}

function buildHotelContextSections(
  order: App.Order,
  offers: Array<OfferTypeForTripItemTypes>,
): Array<HotelBookingContextSection> {
  const offersById = arrayToMap(offers, offer => offer.id)
  const fromLeHotelItems = order.items
    .filter((item) => !isItemCancelled(item))
    .map((orderItem): HotelBookingContextSection => ({
      kind: 'hotel',
      orderId: order.id,
      bookingItemId: orderItem.id,
      offerId: orderItem.offerId,
      property: getLeHotelPropertyName(orderItem, offersById.get(orderItem.offerId)) ?? orderItem.offer.name,
      checkIn: formatMomentInputAsIsoDate(orderItem.reservation?.startDate),
      checkOut: formatMomentInputAsIsoDate(orderItem.reservation?.endDate),
    }))

  const fromBedbankItems = order.bedbankItems
    .filter((item) => !isItemCancelled(item))
    .map((bedbankOrderItem): HotelBookingContextSection => ({
      kind: 'hotel',
      orderId: order.id,
      bookingItemId: bedbankOrderItem.id,
      offerId: bedbankOrderItem.offer.id,
      property: bedbankOrderItem.offer.property?.name ?? bedbankOrderItem.offer.name,
      checkIn: formatMomentInputAsIsoDate(bedbankOrderItem.checkIn),
      checkOut: formatMomentInputAsIsoDate(bedbankOrderItem.checkOut),
    }))

  return [...fromLeHotelItems, ...fromBedbankItems]
}

function buildExperienceContextSections(order: App.Order): Array<ExperienceBookingContextSection> {
  return order.experienceItems
    .filter((item) => !isItemCancelled(item))
    .map((orderExperienceItem) => ({
      kind: 'experience',
      orderId: order.id,
      bookingItemId: orderExperienceItem.id,
      offerId: orderExperienceItem.experienceId,
      experience: orderExperienceItem.title,
      location: orderExperienceItem.redemptionLocation?.name,
      date: formatMomentInputAsIsoDate(orderExperienceItem.date),
      time: orderExperienceItem.time,
    }))
}

async function loadFareFlightGroups(orderId: string, orderFlightItemId: string): Promise<Array<Array<App.FlightV2>>> {
  try {
    const flightDetails = await getFlightDetailsWithDedup({ orderId, itemId: orderFlightItemId })
    return flightDetails.journeyFares.fares
      .map((fare) => fare.flightGroup.flights)
      .filter((fareFlights) => fareFlights.length > 0)
  } catch(error) {
    reportClientError(error, {
      message: 'Failed to load flight details for trip planner add-booking context',
      orderId,
      orderFlightItemId,
    })
    return []
  }
}

async function buildFlightContextSection(
  orderId: string,
  orderFlightItem: App.OrderFlightItem,
): Promise<FlightBookingContextSection> {
  const fareFlightGroups = await loadFareFlightGroups(orderId, orderFlightItem.id)
  const flights = fareFlightGroups.flat()
  const endpoints = firstAndLast(flights)
  const { fromLabel, toLabel } = getRouteEndpointLabels(flights)
  const flightRouteLabels = nonNullable(fareFlightGroups.map(buildFlightRouteLabel))
  const shouldUseRouteSummary =
    flightRouteLabels.length > 1 &&
    !!fromLabel &&
    !!toLabel &&
    fromLabel === toLabel

  return {
    kind: 'flight',
    orderId,
    bookingItemId: orderFlightItem.id,
    reservationId: orderFlightItem.reservationId,
    flightNumbers: flights.map((flight) => flight.flightNumber).filter(Boolean).join(', '),
    tripLegs: shouldUseRouteSummary ? flightRouteLabels.join(' | ') : undefined,
    from: shouldUseRouteSummary ? undefined : fromLabel,
    to: shouldUseRouteSummary ? undefined : toLabel,
    departureDate: formatMomentInputAsIsoDate(endpoints?.first.departingDate ?? orderFlightItem.departureDate),
    arrivalDate: formatMomentInputAsIsoDate(endpoints?.last.arrivalDate ?? orderFlightItem.arrivalDate),
  }
}

async function buildSelectedBookingContextSections({ order, offers }: SelectedBooking): Promise<Array<BookingContextSection>> {
  const flightSections = await Promise.all(
    order.flightItems
      .filter((item) => !isItemCancelled(item))
      .map((orderFlightItem) => buildFlightContextSection(order.id, orderFlightItem)),
  )

  return [
    ...buildHotelContextSections(order, offers),
    ...buildExperienceContextSections(order),
    ...flightSections,
  ]
}

export function getAddBookingsLabel(intl: IntlShape, count: number): string {
  return intl.formatMessage(
    {
      id: 'aaU95e3ktm9-lR2xj0c7',
      defaultMessage:
        'Add {count, plural, one {this booking} other {these bookings}} to my trip',
      description:
        'Visible user chat message sent when the traveller adds selected bookings to their trip from the add booking modal or the trip planner chat landing composer',
    },
    { count },
  )
}

export interface AddBookingsMessageInput {
  selectedBookings: Array<SelectedBooking>
  /** Unscoped orders the selection came from; `selectedBookings` holds item-scoped clones that can't tell whether an order is flight-only. */
  originalOrders: ReadonlyArray<App.Order>
  visibleMessage?: string
}

/** 'cancelled': the chat session changed while the message was being built, so nothing was sent. */
export type AddBookingsOutcome = 'sent' | 'unavailable' | 'cancelled'

/**
 * The visible message plus the hidden booking context the agent needs to add
 * existing bookings to the trip. Undefined when no context could be built.
 */
export async function buildAddBookingsMessage(
  intl: IntlShape,
  { selectedBookings, originalOrders, visibleMessage }: AddBookingsMessageInput,
): Promise<string | undefined> {
  const originalOrdersById = new Map(originalOrders.map(order => [order.id, order]))
  const isFlightOnlySelection =
    selectedBookings.length > 0 &&
    selectedBookings.every(({ order }) => {
      const originalOrder = originalOrdersById.get(order.id)
      return originalOrder ? isFlightOnlyOrderForChat(originalOrder) : false
    })
  const selectedBookedFlights = getSelectedBookedFlights(selectedBookings)

  if (isFlightOnlySelection && selectedBookedFlights.length > 0) {
    const displayMessage = visibleMessage ?? intl.formatMessage(
      {
        id: 'iKGhYCknbCBBRG6uYS58',
        defaultMessage:
          'Add {count, plural, one {this booked flight} other {these booked flights}} to my trip.',
        description:
          'Visible user chat message sent after selecting one or more existing booked flights from the add booking modal in trip planner chat',
      },
      { count: selectedBookedFlights.length },
    )
    return `${displayMessage}\n${serialiseBookedFlightContext(selectedBookedFlights)}`
  }

  const sectionsPerBooking = await Promise.all(selectedBookings.map(buildSelectedBookingContextSections))
  const bookingContextSections = sectionsPerBooking.flat()
  if (bookingContextSections.length === 0) {
    return undefined
  }

  return appendContextTag(
    visibleMessage ?? getAddBookingsLabel(intl, selectedBookings.length),
    serialiseBookingContext(bookingContextSections),
    TRIP_PLANNER_ACTION_CONTEXT_LABEL,
  )
}

export type SelectableBooking =
  | { bookingType: 'hotel', item: App.OrderItem }
  | { bookingType: 'bedbank', item: App.OrderBedbankItem }
  | { bookingType: 'flight', item: App.OrderFlightItem }
  | { bookingType: 'experience', item: App.OrderExperienceItem }

/** The order's active bookings that can be added to a chat, one per item. */
export function getSelectableBookings(order: App.Order): Array<SelectableBooking> {
  return [
    ...order.items.filter(item => !isItemCancelled(item)).map((item): SelectableBooking => ({ bookingType: 'hotel', item })),
    ...order.bedbankItems.filter(item => !isItemCancelled(item)).map((item): SelectableBooking => ({ bookingType: 'bedbank', item })),
    ...order.flightItems.filter(item => !isItemCancelled(item)).map((item): SelectableBooking => ({ bookingType: 'flight', item })),
    ...order.experienceItems.filter(item => !isItemCancelled(item)).map((item): SelectableBooking => ({ bookingType: 'experience', item })),
  ]
}

function getScopedItems(booking: SelectableBooking): ScopedOrderItems {
  switch (booking.bookingType) {
    case 'hotel':
      return { items: [booking.item] }
    case 'bedbank':
      return { bedbankItems: [booking.item] }
    case 'flight':
      return { flightItems: [booking.item] }
    case 'experience':
      return { experienceItems: [booking.item] }
  }
}

/** A copy of the order holding only the booking's item, which the booking cards and context builders read. */
export function buildBookingScopedOrder(order: App.Order, booking: SelectableBooking): App.Order {
  return buildScopedOrder(order, getScopedItems(booking))
}

export function toSelectedBooking(
  order: App.Order,
  booking: SelectableBooking,
  offers: Array<OfferTypeForTripItemTypes> = [],
): SelectedBooking {
  return {
    order: buildBookingScopedOrder(order, booking),
    offers,
    selectionKey: `${booking.bookingType}:${order.id}:${booking.item.id}`,
    bookingItemId: booking.item.id,
  }
}

export function collectSelectedBookings(
  orders: ReadonlyArray<App.Order>,
  itemIds: ReadonlySet<string>,
): Array<SelectedBooking> {
  return orders.flatMap(order =>
    getSelectableBookings(order)
      .filter(booking => itemIds.has(booking.item.id))
      .map(booking => toSelectedBooking(order, booking)),
  )
}
